import { SxProp, useDripsyTheme } from 'dripsy';
import { useMemo } from 'react';
import { useStore } from '../../../../store/store';

export enum ButtonVariant {
  primary = 'primary',
  secondary1 = 'secondary1',
  secondary2 = 'secondary2',
  outline = 'outline',
  outline1 = 'outline1',
  disabled = 'disabled'
}

export enum ButtonSize {
  default = 'default',
  small = 'small',
  medium = 'medium',
  large = 'large'
}

export enum ButtonState {
  default = 'Default',
  pressed = 'Pressed'
}

interface UseButtonProps {
  pressed: boolean;
  size?: ButtonSize;
  variant: ButtonVariant;
  loading?: boolean;
  disabled?: boolean;
}

interface UseButton {
  buttonStyle: SxProp;
  backgroundGradients: Array<string>;
  labelStyle: SxProp;
  iconColor: string;
}

export const useButton = (props: UseButtonProps): UseButton => {
  const { variant = ButtonVariant.primary, size = ButtonSize.default, pressed, loading, disabled } = props;

  const { theme } = useDripsyTheme();
  const buttonVariant = useMemo(() => (disabled ? ButtonVariant.disabled : variant), [variant, loading, disabled]);
  const state = pressed ? ButtonState.pressed : ButtonState.default;

  const buttonSizeStyle = theme.buttons?.[size] || { height: 44 };
  const backgroundGradients = theme.linearGradients?.[`${buttonVariant}${state}`] || ['transparent', 'transparent'];

  const defaultBorderColor = {
    default: 'transparent',
    pressed: 'transparent'
  };

  const borderColors = {
    primary: {
      default: defaultBorderColor,
      pressed: defaultBorderColor
    },
    secondary1: {
      default: theme.colors?.$textButtonOutline,
      pressed: theme.colors?.$textButtonOutline
    },
    secondary2: {
      default: defaultBorderColor,
      pressed: defaultBorderColor
    },
    disabled: {
      default: defaultBorderColor,
      pressed: defaultBorderColor
    },
    outline: {
      default: theme.colors?.$primary,
      pressed: theme.colors?.$textButtonOutline
    },
    outline1: {
      default: theme.colors?.$primary,
      pressed: theme.colors?.$textButtonOutline
    }
  };
  const borderColor = borderColors[buttonVariant][state.toLowerCase()] || 'transparent';

  const buttonStyle: SxProp = {
    ...theme.buttons?.base,
    ...buttonSizeStyle,
    borderWidth: [ButtonVariant.secondary1, ButtonVariant.outline, ButtonVariant.outline1].includes(buttonVariant) ? 1 : 0,
    borderColor
  };

  const textColors = {
    [ButtonVariant.primary]: {
      default: theme.colors?.$textButtonPrimary,
      pressed: theme.colors?.$textButtonPrimary
    },
    [ButtonVariant.secondary1]: {
      default: theme.colors?.$textButtonOutline,
      pressed: theme.colors?.$textButtonOutline
    },
    [ButtonVariant.secondary2]: {
      default: theme.colors?.$textButtonSecondary,
      pressed: theme.colors?.$textButtonSecondary
    },
    [ButtonVariant.outline]: {
      // default: theme.colors?.[store.theme === 'light' ? '$textButtonSecondary' : '$textButtonPrimary'],
      default: theme.colors?.$primary,
      pressed: theme.colors?.$textButtonOutline
    },
    [ButtonVariant.outline1]: {
      default: theme.colors?.$textPrimary,
      pressed: theme.colors?.$textPrimary
    },
    [ButtonVariant.disabled]: {
      default: theme.colors?.$textDisable,
      pressed: theme.colors?.$textDisable
    }
  };

  const defaultButtonTextStyle: SxProp = { ...theme.text?.textBody, fontWeight: theme.fontWeights?.bold };
  const textSize = {
    default: defaultButtonTextStyle,
    small: theme.text?.textCaption,
    medium: defaultButtonTextStyle,
    large: defaultButtonTextStyle
  };

  const textColor = textColors[buttonVariant][pressed ? 'pressed' : 'default'];
  const iconColor = textColor;
  let labelStyle = {
    ...textSize[size],
    color: textColor
  };

  return {
    buttonStyle,
    backgroundGradients,
    labelStyle,
    iconColor
  };
};
